import React from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import colors from '../../config/colors';

const TextAreaWithSuggestions = ({ question, value, onChange }) => {
  const charCount = value.length;
  const minLength = question.minLength || 0;
  const showCount = minLength > 0;

  const handleSuggestionClick = (suggestion) => {
    // Add suggestion to existing text with proper spacing
    const newValue = value 
      ? `${value.trim()} ${suggestion}` 
      : suggestion;
    onChange(newValue);
  };

  return (
    <div style={{ padding: '12px 0' }}>
      <h2 style={{
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: '700',
        color: colors.textPrimary,
        marginBottom: '8px',
        lineHeight: '1.2'
      }}>
        {question.question}
      </h2>

      {question.subtitle && (
        <p style={{
          fontSize: '1rem',
          color: colors.textSecondary,
          marginBottom: '20px'
        }}>
          {question.subtitle}
        </p>
      )}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder}
        rows={6}
        style={{
          width: '100%',
          padding: '18px 20px',
          fontSize: '1.05rem',
          lineHeight: '1.6',
          border: `2px solid ${colors.borderMedium}`,
          borderRadius: '12px',
          background: colors.bgCard,
          color: colors.textPrimary,
          outline: 'none',
          transition: 'all 0.3s ease',
          fontFamily: 'inherit',
          resize: 'vertical'
        }}
        onFocus={(e) => e.target.style.borderColor = colors.brandPrimary}
        onBlur={(e) => e.target.style.borderColor = colors.borderMedium}
      />

      {showCount && (
        <div style={{
          textAlign: 'right',
          fontSize: '0.85rem',
          color: charCount >= minLength ? colors.textMuted : colors.brandPrimary,
          marginTop: '8px'
        }}>
          {charCount} / {minLength} characters
        </div>
      )}

      {/* Suggestion Tags */}
      {question.suggestions && question.suggestions.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: '600',
            color: colors.textSecondary,
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>💭 Need ideas? Click to add:</span>
          </div>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            {question.suggestions.map((suggestion) => (
              <motion.button
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: colors.bgCard,
                  border: `2px solid ${colors.borderMedium}`,
                  borderRadius: '20px',
                  padding: '10px 16px',
                  fontSize: '0.9rem',
                  color: colors.textPrimary,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = colors.brandPrimary;
                  e.target.style.background = colors.bgAccent;
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = colors.borderMedium;
                  e.target.style.background = colors.bgCard;
                }}
              >
                <Plus size={14} />
                {suggestion}
              </motion.button>
            ))}
          </div>
          
          <p style={{
            fontSize: '0.8rem',
            color: colors.textMuted,
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            Click any phrase to add it to your answer. Feel free to edit and personalize.
          </p>
        </div>
      )}
    </div>
  );
};

export default TextAreaWithSuggestions;
